import Head from 'next/head';
import { getMetadataTags } from 'lib/utils/get-metadata-tags';
import { buildAggregateRating } from 'lib/utils/build-aggregate-rating';

interface ProductHeadProps {
  productName: string;
  productImages: string[];
  productDescription: string;
  productSku: string;
  productGtin?: string;
  canonicalUrl: string;
  productPrice: string;
  salePrice?: string | null;
  priceValidUntil?: string | null;
  pageTitle: string;
  ogImageUrl: string;
  ogImageAlt: string;
  enableRecaptcha?: boolean;
  faqSchema?: object;
  yotpoAverageScore?: string | number | null;
  yotpoTotalReviews?: string | number | null;
}

function buildStructuredData({
  productName,
  productImages,
  productDescription,
  productSku,
  productGtin,
  canonicalUrl,
  productPrice,
  salePrice,
  priceValidUntil,
  yotpoAverageScore,
  yotpoTotalReviews,
}: Omit<
  ProductHeadProps,
  'pageTitle' | 'ogImageUrl' | 'ogImageAlt' | 'enableRecaptcha' | 'faqSchema'
>) {
  const aggregateRating = buildAggregateRating(
    yotpoAverageScore,
    yotpoTotalReviews,
  );
  // Sale pricing follows Google's merchant listing rules: the active price
  // (offers.price) must stay untagged, and the original price is carried as a
  // separate StrikethroughPrice spec — not as a discount on the active price.
  const isOnSale = Boolean(salePrice);
  const activePrice = isOnSale ? (salePrice as string) : productPrice;
  const priceSpecification = isOnSale
    ? {
        '@type': 'UnitPriceSpecification',
        priceType: 'https://schema.org/StrikethroughPrice',
        price: productPrice,
        priceCurrency: 'USD',
      }
    : {
        '@type': 'UnitPriceSpecification',
        price: productPrice,
        priceCurrency: 'USD',
        priceType: 'http://schema.org/MSRP',
      };
  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: productName,
    image: productImages,
    description: productDescription,
    sku: productSku,
    ...(productGtin ? { gtin: productGtin } : {}),
    ...(aggregateRating ? { aggregateRating } : {}),
    brand: {
      '@type': 'Brand',
      name: 'Seed',
    },
    offers: {
      '@type': 'Offer',
      url: canonicalUrl,
      price: activePrice,
      priceCurrency: 'USD',
      ...(isOnSale && priceValidUntil ? { priceValidUntil } : {}),
      itemCondition: 'http://schema.org/NewCondition',
      availability: 'http://schema.org/InStock',
      acceptedPaymentMethod: [
        { '@type': 'PaymentMethod', name: 'Credit Card' },
        { '@type': 'PaymentMethod', name: 'PayPal' },
        { '@type': 'PaymentMethod', name: 'Apple Pay' },
        { '@type': 'PaymentMethod', name: 'Amazon Pay' },
      ],
      priceSpecification,
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'USD',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          businessDays: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'http://schema.org/Monday',
              'http://schema.org/Tuesday',
              'http://schema.org/Wednesday',
              'http://schema.org/Thursday',
              'http://schema.org/Friday',
            ],
          },
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: '0',
            maxValue: '1',
            unitCode: 'd',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: '3',
            maxValue: '8',
            unitCode: 'd',
          },
        },
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        merchantReturnLink: `https://help${process.env.NEXT_PUBLIC_COOKIE_DOMAIN}`,
        description:
          '30 Day Money-Back Guarantee on First Order without Product Return',
        merchantReturnDays: '30',
        returnFees: '0',
      },
    },
  };
}

export default function ProductHead({
  productName,
  productImages,
  productDescription,
  productSku,
  productGtin,
  canonicalUrl,
  productPrice,
  salePrice,
  priceValidUntil,
  pageTitle,
  ogImageUrl,
  ogImageAlt,
  enableRecaptcha = false,
  faqSchema,
  yotpoAverageScore,
  yotpoTotalReviews,
}: ProductHeadProps) {
  const ldData = buildStructuredData({
    productName,
    productImages,
    productDescription,
    productSku,
    productGtin,
    canonicalUrl,
    productPrice,
    salePrice,
    priceValidUntil,
    yotpoAverageScore,
    yotpoTotalReviews,
  });

  return (
    <Head>
      <title>{pageTitle}</title>
      {getMetadataTags({
        pageTitle,
        pageDescription: productDescription,
        canonicalUrl,
        imageUrl: ogImageUrl,
        ogImageAlt,
      })}
      {enableRecaptcha && (
        <script src='https://www.google.com/recaptcha/api.js' async defer />
      )}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldData) }}
      />
      {faqSchema && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
    </Head>
  );
}
